import { Injectable } from "@angular/core";
import { SortDirection } from "@app/enum/SortDirection";
import { IJourney } from "@app/interfaces/IJourney";
import { DateService } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { VideoStreamType } from "../enums/VideoStreamType";
import { ICameraFootageChannelTimeline, ICameraFootageTimelineDisplaySection, IFootageSectionGap, ITimelineBase, ITimelineChannelSummary, ITimelineSummary } from "../models/CameraFootageTimeline.interfaces";
import { IAvailableDailyFootage } from "../models/IAvailableDailyFootage";



@Injectable({
    providedIn: 'root'
})
export class CameraFootageTimelineService {

    constructor(
        private readonly dateService: DateService,
        private readonly translationService: TranslationService
    ) { }

    trimEndDateForRequestUsingSummary(summary: ITimelineSummary, startDate: Date, endDate: Date, requiredQuality: VideoStreamType): Date {

        if (!summary?.displaySections?.length) {
            console.warn("No summary sections to search, returning existing end date");
            return endDate;
        }

        const startSection = this.getTimelineDisplaySectionAtDate(summary.displaySections, startDate);

        if (!startSection) {
            console.warn("Couldn't find necessary section to trim date, returning existing end date");
            return endDate;
        }

        if (!startSection.availableVideoStreamTypes.includes(requiredQuality)) {
            console.warn("Start section didn't include required quality, returning existing end date");
            return endDate;
        }

        if (this.dateService.dateIsBetweenDates(endDate, startSection.dateStart, startSection.dateEnd, true, true)) {
            return endDate;
        }

        const indexOfSection = summary.displaySections.indexOf(startSection);

        let latestSection = startSection;

        for (var i = indexOfSection + 1; i < summary.displaySections.length; i++) {
            const section = summary.displaySections[i];

            if (!section.availableVideoStreamTypes.includes(requiredQuality)) {
                break;
            }

            if (!this.dateService.date1IsAfterDate2(endDate, section.dateStart)) {
                break; 
            }

            latestSection = section;
        }


        const trimmedDate: Date = (this.dateService.date1IsAfterDate2(endDate, latestSection.dateEnd))
            ? new Date(latestSection.dateEnd)
            : endDate;

        return trimmedDate;
    }

    getTimelineDisplaySectionAtDate(sections: ICameraFootageTimelineDisplaySection[], startDate: Date): ICameraFootageTimelineDisplaySection {
        if (!sections?.length) {
            return;
        }

        return sections.find(section => this.dateService.dateIsBetweenDates(startDate, section.dateStart, section.dateEnd, true, true));
    }

    createAvailableDailyFootageTimelineFromJourneys(date: Date, journeys: IJourney[]): ITimelineSummary {
        if (!journeys?.length) {
            return;
        }

        const availableDailyFootageFromJourneys = this.convertJourneysIntoAvailableDailyFootage(journeys);

        return this.createAvailableDailyFootageTimeline(date, availableDailyFootageFromJourneys);
    }

    createAvailableDailyFootageTimeline(date: Date, availableDailyFootage: IAvailableDailyFootage[]): ITimelineSummary {
        if (!availableDailyFootage?.length) {
            return;
        }

        const summaryStartDate = this.dateService.getStartOfDay(date);
        const summaryEndDate = this.dateService.getEndOfDay(date);

        const summary: ITimelineSummary = this.createTimelineSummaryTemplate([...availableDailyFootage], summaryStartDate, summaryEndDate);

        if (!summary.footage?.length) {
            return;
        }

        summary.channelSummaries = this.createChannelOverviews(summary);
        summary.availableChannelIds = summary.channelSummaries.flatMap(channelSummary => channelSummary.availableChannelIds);
        summary.availableVideoStreamTypes = this.getVideoStreamTypesFromFootage(summary.footage);

        const tolerance: number = 300;

        summary.displaySections = this.createFootageDisplaySections(summary.footage, summary.footage[0].Start, summary.footage[0].End, null, tolerance);
        this.patch(summary.displaySections);

        this.setFootageSectionPercentages(summary.displaySections);
        this.setStartEndDatesFromFootage(summary);

        return summary;
    }

    setFootageSectionPercentages(sections: ICameraFootageTimelineDisplaySection[]): void {

        if (!sections?.length) {
            return;
        }

        sections.forEach(section => {

            const startPercentage = this.getFootagePositionFromTime(section.dateStart);
            const endPercentage = this.getFootagePositionFromTime(section.dateEnd);

            section.startPercentage = startPercentage;
            section.widthPercentage = endPercentage - startPercentage;

            // Ensure we cannot go below 0% left
            if (section.startPercentage < 0) {
                section.startPercentage = 0;
            }

            // Ensure that we cannot go over 100%
            if (section.startPercentage + section.widthPercentage > 100) {
                section.widthPercentage -= section.startPercentage + section.widthPercentage - 100;
            }
        });
    }

    // ---

    private convertJourneysIntoAvailableDailyFootage(journeys: IJourney[]): IAvailableDailyFootage[] {
        const availableDailyFootage: IAvailableDailyFootage[] = [];

        if (!journeys?.length) {
            return availableDailyFootage;
        }

        journeys.forEach(journey => {
            availableDailyFootage.push({
                End: new Date(journey.EndUtc),
                Start: new Date(journey.StartUtc),
                StreamType: VideoStreamType.Main
            });
        })

        return availableDailyFootage;
    }

    private createChannelOverviews(overview: ITimelineSummary): ITimelineChannelSummary[] {
        const channelOverviews: ITimelineChannelSummary[] = [];
        const channelFootageMap = this.getChannelFootageMapFromSummary(overview);

        if (!channelFootageMap?.size) {
            return channelOverviews;
        }

        channelFootageMap.forEach((footage: IAvailableDailyFootage[], channelId: number) => {
            channelOverviews.push(this.createChannelOverview(channelId, footage));
        });

        return channelOverviews;
    }

    private createChannelOverview(channelId: number, footage: IAvailableDailyFootage[]): ITimelineChannelSummary {
        const channelOverview = this.createChannelOverviewTemplate(channelId);

        channelOverview.footage = footage;
        channelOverview.channelTimelines = this.createChannelTimelines(channelOverview);
        channelOverview.availableVideoStreamTypes = this.getVideoStreamTypesFromFootage(channelOverview.footage);

        channelOverview.displaySections = this.createFootageDisplaySections(channelOverview.footage, channelOverview.footage[0].Start, channelOverview.footage[0].End);
        this.patch(channelOverview.displaySections);
        this.setFootageSectionPercentages(channelOverview.displaySections);
        this.setStartEndDatesFromFootage(channelOverview);

        return channelOverview;
    }

    private createChannelTimelines(overview: ITimelineChannelSummary): ICameraFootageChannelTimeline[] {
        const channelTimelines: ICameraFootageChannelTimeline[] = [];
        const channelFootageMap = this.getChannelFootageMapFromSummary(overview);

        if (!channelFootageMap?.size) {
            return channelTimelines;
        }

        channelFootageMap.forEach((footage: IAvailableDailyFootage[], channelId: number) => {
            const footageByVideoStreamType = this.getChannelStreamTypeFootageMap(footage);

            footageByVideoStreamType.forEach((footage: IAvailableDailyFootage[], streamType: VideoStreamType) => {
                channelTimelines.push(this.createChannelTimeline(channelId, footage, streamType));
            });
        });

        return channelTimelines;
    }

    private createChannelTimeline(channelId: number, footage: IAvailableDailyFootage[], streamType: VideoStreamType): ICameraFootageChannelTimeline {

        const channelTimeline = this.createChannelTimelineTemplate(channelId);

        channelTimeline.availableVideoStreamTypes = [streamType];
        channelTimeline.footage = footage;

        this.setStartEndDatesFromFootage(channelTimeline);

        channelTimeline.displaySections = this.createChannelTimelineDisplaySections(channelTimeline);

        return channelTimeline;
    }

    private createChannelTimelineDisplaySections(channelTimeline: ICameraFootageChannelTimeline): ICameraFootageTimelineDisplaySection[] {
        const sections: ICameraFootageTimelineDisplaySection[] = [];

        if (!channelTimeline?.footage?.length) {
            return sections;
        }

        channelTimeline.footage.forEach(footage => {

            const newSection: ICameraFootageTimelineDisplaySection = {
                dateStart: footage.Start,
                dateEnd: footage.End,
                footage: [footage],
                availableVideoStreamTypes: [footage.StreamType],
                availableChannelIds: [footage.Channel],
                label: this.translationService.t("Common.Online")
            }

            newSection.hasMainStream = newSection.availableVideoStreamTypes.includes(VideoStreamType.Main);
            newSection.hasSubStream = newSection.availableVideoStreamTypes.includes(VideoStreamType.Sub);

            sections.push(newSection);
        });

        this.setFootageSectionPercentages(sections);

        return sections;
    }


    private createBaseTimelineTemplate(startDate: Date = null, endDate: Date = null): ITimelineBase {
        const newTemplate: ITimelineBase = {
            dateStart: startDate,
            dateEnd: endDate,

            availableVideoStreamTypes: [],
            availableChannelIds: [],
            footage: [],

            displaySections: []
        }

        return newTemplate;
    }

    private createChannelOverviewTemplate(channelId: number, startDate: Date = null, endDate: Date = null): ITimelineChannelSummary {
        const newTemplate: ITimelineChannelSummary = {
            ...this.createBaseTimelineTemplate(startDate, endDate),

            channelId: channelId,
            channelTimelines: [],
            availableChannelIds: [channelId]
        }

        return newTemplate;
    }

    private createTimelineSummaryTemplate(footage: IAvailableDailyFootage[], startDate: Date, endDate: Date): ITimelineSummary {

        let filteredFootage = this.filterFootageWithinDates(footage, startDate, endDate);

        this.orderFootageByDateProperty(filteredFootage, "Start");

        const newTemplate: ITimelineSummary = {
            ...this.createBaseTimelineTemplate(startDate, endDate),

            footage: filteredFootage,
            channelSummaries: []
        }

        this.setStartEndDatesFromFootage(newTemplate);

        return newTemplate;
    }

    private createChannelTimelineTemplate(channelId: number): ICameraFootageChannelTimeline {
        const newTemplate: ICameraFootageChannelTimeline = {
            ...this.createBaseTimelineTemplate(),
            channelId: channelId
        }

        newTemplate.availableChannelIds = [channelId];

        return newTemplate;
    }

    private getChannelFootageMapFromSummary(summary: ITimelineSummary | ITimelineChannelSummary): Map<number, IAvailableDailyFootage[]> {
        const channelFootageMap = summary.footage.reduce((channelMap, footage) => {
            const channelArray = channelMap.get(footage.Channel) || [];

            channelArray.push(footage);
            channelMap.set(footage.Channel, channelArray);

            return channelMap;
        }, new Map<number, IAvailableDailyFootage[]>());

        return channelFootageMap;
    }

    private getChannelStreamTypeFootageMap(footage: IAvailableDailyFootage[]): Map<VideoStreamType, IAvailableDailyFootage[]> {
        const channelFootageMap = footage.reduce((channelMap, footage) => {
            const channelArray = channelMap.get(footage.StreamType) || [];

            channelArray.push(footage);
            channelMap.set(footage.StreamType, channelArray);

            return channelMap;
        }, new Map<VideoStreamType, IAvailableDailyFootage[]>());

        return channelFootageMap;
    }

    private getVideoStreamTypesFromFootage(footage: IAvailableDailyFootage[]): VideoStreamType[] {
        if (!footage?.length) {
            return [];
        }

        return [...new Set(footage.map(footage => footage.StreamType))];
    }

    private filterFootageWithinDates(footage: IAvailableDailyFootage[], startDate: Date, endDate: Date): IAvailableDailyFootage[] {
        if (!footage?.length) {
            return [];
        }

        footage.forEach(footage => {
            footage.Start = footage.Start < startDate ? startDate : footage.Start;
            footage.End = footage.End > endDate ? endDate : footage.End;
        })

        return footage.filter(footage => {
            return this.dateService.dateIsBetweenDates(footage.Start, startDate, endDate, true, true)
                && this.dateService.dateIsBetweenDates(footage.End, startDate, endDate, true, true)
        });
    }

    private orderFootageByDateProperty(footage: IAvailableDailyFootage[], property: string): void {
        if (!footage?.length) {
            return;
        }

        footage.sort((footage1, footage2) => this.dateService.sort(footage1[property], footage2[property], SortDirection.Ascending));
    }

    private orderSectionsByDateProperty(sections: ICameraFootageTimelineDisplaySection[], property: string): void {
        if (!sections?.length) {
            return;
        }

        sections.sort((section1, section2) => this.dateService.sort(section1[property], section2[property], SortDirection.Ascending));
    }

    private setStartEndDatesFromFootage(timelineBase: ITimelineBase): void {
        if (!timelineBase?.footage?.length) {
            return;
        }

        timelineBase.dateStart = new Date(timelineBase.footage[0].Start);
        timelineBase.dateEnd = new Date(this.findFootageWithLatestEndDate(timelineBase.footage)?.End);
    }

    private findFootageWithLatestEndDate(footage: IAvailableDailyFootage[]): IAvailableDailyFootage {
        if (!footage?.length) {
            return null;
        }

        return footage.reduce((latest, current) => {
            return current.End > latest.End ? current : latest;
        }, footage[0]);
    }

    private findFootageWithEarliestEndDate(footage: IAvailableDailyFootage[]): IAvailableDailyFootage {
        if (!footage?.length) {
            return null;
        }

        return footage.reduce((latest, current) => {
            return current.End > latest.End ? latest : current;
        }, footage[0]);
    }

    private getFootagePositionFromTime(date: Date): number {

        const percentPerSecond = 100 / (24 * 60 * 60);
        let position = this.dateService.getSecondOfDay(date) * percentPerSecond;

        position = Math.max(0, position);
        position = Math.min(100, position);

        return position;
    }

    private createFootageDisplaySections(availableFootage: IAvailableDailyFootage[], startDate: Date = null, endDate: Date = null, gap: IFootageSectionGap = null, tolerance: number = null): ICameraFootageTimelineDisplaySection[] {
        const sections: ICameraFootageTimelineDisplaySection[] = [];

        if (!availableFootage?.length) {
            return sections;
        }

        sections.push(this.createNewSection(availableFootage[0], startDate, endDate));

        availableFootage.forEach((footage, index) => {

            const currentSection = sections[sections.length - 1];
            const footageInCurrentSection: boolean = currentSection.footage.includes(footage);

            if (footageInCurrentSection) {
                return;
            }

            if (this.dateService.isSameDate(footage.Start, currentSection.dateStart, true) && !footageInCurrentSection) {
                this.addFootageToSection(currentSection, footage);
            }

            const footageStartAtCurrentSectionEnd: boolean = this.dateService.isSameDate(footage.Start, currentSection.dateEnd, true);
            const footageStartAfterCurrentSectionEnd: boolean = this.dateService.date1IsAfterDate2(footage.Start, currentSection.dateEnd);
            const footageStartAfterCurrentSectionStart: boolean = this.dateService.date1IsAfterDate2(footage.Start, currentSection.dateStart);
            const footageStartsBeforeCurrentSectionStart: boolean = this.dateService.date1IsAfterDate2(currentSection.dateStart, footage.Start);
            const footageStartsBeforeCurrentSectionEnd: boolean = this.dateService.date1IsAfterDate2(currentSection.dateEnd, footage.Start);
            const footageEndsBeforeCurrentSectionEnd: boolean = this.dateService.date1IsAfterDate2(currentSection.dateEnd, footage.End);
            const footageChannelInCurrentSection: boolean = currentSection.availableChannelIds.includes(footage.Channel);
            const footageStreamTypeInCurrentSection: boolean = currentSection.availableVideoStreamTypes.includes(footage.StreamType);
            const footageSharesCharacteristics: boolean = footageChannelInCurrentSection && footageStreamTypeInCurrentSection;

            let newSeparateSection: ICameraFootageTimelineDisplaySection;
            let newIntersectionSection: ICameraFootageTimelineDisplaySection;

            if (!footageStartAfterCurrentSectionStart && !gap) {
                return;
            }

            if (!!gap) {
                this.addFootageToSection(currentSection, footage, gap);

            } else if (footageEndsBeforeCurrentSectionEnd || footageStartsBeforeCurrentSectionEnd || footageStartsBeforeCurrentSectionStart) {
                if (footageSharesCharacteristics) {
                    this.addFootageToSection(currentSection, footage, gap);
                } else {
                    newIntersectionSection = this.createNewIntersectingSection(currentSection, footage, gap);
                    sections.push(newIntersectionSection);
                }
            } else if (footageStartAtCurrentSectionEnd && footageSharesCharacteristics) {
                this.extendFootageSection(currentSection, footage, gap);

            } else if (footageStartAtCurrentSectionEnd || footageStartAfterCurrentSectionEnd) {
                newSeparateSection = this.createNewSection(footage);
                sections.push(newSeparateSection);

                const footageToExtendToNewSection = currentSection.footage.filter(currentSectionFootage => {
                    return this.dateService.date1IsAfterDate2(currentSectionFootage.End, newSeparateSection.dateStart);
                });

                footageToExtendToNewSection?.forEach(extendableFootage => this.addFootageToSection(newSeparateSection, extendableFootage));
            }
        });

        sections.forEach(section => {
            section.hasMainStream = section.availableVideoStreamTypes.includes(VideoStreamType.Main);
            section.hasSubStream = section.availableVideoStreamTypes.includes(VideoStreamType.Sub);
        });

        return sections;
    }

    private patch(sections: ICameraFootageTimelineDisplaySection[]): void {

        const gaps: IFootageSectionGap[] = this.getGaps(sections);

        if (!gaps?.length) {
            return;
        }

        this.patchGaps(sections);

        const postGaps: IFootageSectionGap[] = this.getGaps(sections);

        if (!postGaps?.length) {
            return;
        }

        if (!postGaps.filter(gap => gap.footage.length)?.length) {
            return;
        }

        this.patch(sections);
    }

    private patchGaps(sections: ICameraFootageTimelineDisplaySection[], tolerance: number = null): void {
        const gaps: IFootageSectionGap[] = this.getGaps(sections);
        let newSections: ICameraFootageTimelineDisplaySection[] = [];

        if (!gaps?.length) {
            return;
        }

        gaps.forEach(gap => {
            const newSectionsForGap = this.createFootageDisplaySections(gap.footage, gap.dateStart, gap.dateEnd, gap, tolerance);

            if (!newSectionsForGap?.length) {
                return;
            }

            newSections.push(...newSectionsForGap);
        });

        sections.push(...newSections);
        this.orderSectionsByDateProperty(sections, "dateStart");
    }

    private getGaps(sections: ICameraFootageTimelineDisplaySection[]): IFootageSectionGap[] {
        const gaps: IFootageSectionGap[] = [];

        sections.forEach((section, index) => {

            if (index === 0) {
                return;
            }

            const previousSection = sections[index - 1];
            const previousSectionEnd: Date = previousSection.dateEnd;
            const sectionStart = section.dateStart;

            if (!this.dateService.date1IsAfterDate2(sectionStart, previousSectionEnd)) {
                return;
            }

            const footage = previousSection.footage.filter(footage => this.dateService.date1IsAfterDate2(footage.End, previousSectionEnd));

            if (!footage?.length) {
                return;
            }

            const earliestFootageDateEnd = this.findFootageWithEarliestEndDate(footage)?.End;
            const nextSectionDateStart = sections.find(section => this.dateService.date1IsAfterDate2(section.dateStart, previousSectionEnd))?.dateStart;

            let dateEnd = earliestFootageDateEnd;

            if (!!nextSectionDateStart && this.dateService.date1IsAfterDate2(earliestFootageDateEnd, nextSectionDateStart)) {
                dateEnd = nextSectionDateStart;
            }

            const newGap: IFootageSectionGap = {
                dateStart: previousSectionEnd,
                dateEnd: dateEnd,
                footage: footage
            };

            gaps.push(newGap);
        });

        // area from last section to end of day
        const lastSection = sections[sections.length - 1];
        const lastSectionEnd: Date = lastSection.dateEnd;
        const endOfDay = this.dateService.getEndOfDay(sections[0].dateStart);

        if (this.dateService.date1IsAfterDate2(endOfDay, lastSectionEnd)) {

            const lastSectionFootage = lastSection.footage.filter(footage => this.dateService.date1IsAfterDate2(footage.End, lastSectionEnd));

            if (!lastSectionFootage?.length) {
                return gaps;
            }

            const earliestEndDateFootage = this.findFootageWithEarliestEndDate(lastSectionFootage);

            gaps.push({
                dateStart: lastSectionEnd,
                dateEnd: earliestEndDateFootage.End,
                footage: lastSectionFootage
            });
        }

        return gaps;
    }

    private createNewIntersectingSection(currentSection: ICameraFootageTimelineDisplaySection, footage: IAvailableDailyFootage, gap: IFootageSectionGap = null): ICameraFootageTimelineDisplaySection {
        const newIntersectionSection = this.createNewSection(footage, gap?.dateStart);

        currentSection.dateEnd = new Date(newIntersectionSection.dateStart);

        const footageToExtendToNewSection = currentSection.footage.filter(currentSectionFootage => {
            return this.dateService.date1IsAfterDate2(currentSectionFootage.End, newIntersectionSection.dateStart);
        });

        footageToExtendToNewSection?.forEach(extendableFootage => this.addFootageToSection(newIntersectionSection, extendableFootage));

        return newIntersectionSection;
    }

    private addFootageToSection(section: ICameraFootageTimelineDisplaySection, footage: IAvailableDailyFootage, gap: IFootageSectionGap = null): void {
        section.footage.push(footage);

        if (!section.availableChannelIds.includes(footage.Channel)) {
            section.availableChannelIds.push(footage.Channel);
        }

        if (!section.availableVideoStreamTypes.includes(footage.StreamType)) {
            section.availableVideoStreamTypes.push(footage.StreamType);
        }

        this.trimSectionEndDateIfEarlier(section, footage, gap);
    }

    private extendFootageSection(section: ICameraFootageTimelineDisplaySection, footage: IAvailableDailyFootage, gap: IFootageSectionGap = null): void {
        this.addFootageToSection(section, footage, gap);

        const footageTails = section.footage.filter(sectionFootage => this.dateService.date1IsAfterDate2(sectionFootage.End, section.dateEnd));

        if (footageTails?.length) {
            const earliestTailEnd = this.findFootageWithEarliestEndDate(footageTails);

            if (this.dateService.date1IsAfterDate2(footage.End, earliestTailEnd.End)) {
                section.dateEnd = new Date(earliestTailEnd.End);
                return;
            }
        }

        section.dateEnd = new Date(footage.End);
    }

    private trimSectionEndDateIfEarlier(section: ICameraFootageTimelineDisplaySection, footage: IAvailableDailyFootage, gap: IFootageSectionGap = null): void {
        if (!this.dateService.date1IsAfterDate2(section.dateEnd, footage.End)) {
            return;
        }

        section.dateEnd = new Date(footage.End);
    }

    private createNewSection(footage: IAvailableDailyFootage, overrideDateStart: Date = null, overrideDateEnd: Date = null): ICameraFootageTimelineDisplaySection {

        const newSection: ICameraFootageTimelineDisplaySection = {
            dateStart: new Date(overrideDateStart ?? footage.Start),
            dateEnd: new Date(overrideDateEnd ?? footage.End),
            footage: [footage],
            availableVideoStreamTypes: [footage.StreamType],
            availableChannelIds: [footage.Channel],
            label: this.translationService.t("Common.Online")
        };

        newSection.hasMainStream = newSection.availableVideoStreamTypes.includes(VideoStreamType.Main);
        newSection.hasSubStream = newSection.availableVideoStreamTypes.includes(VideoStreamType.Sub);

        return newSection;
    }
}
